<!--
  Light/dark color theme toggle button.

  Summary: Small icon button that lets the user switch between light and
  dark color themes, usable inline or as a floating fixed-position button.

  Responsibilities / Role:
  - Displays a sun or moon icon (lucide-vue-next) reflecting the current
    theme state.
  - Calls toggleColorTheme on click to flip the app-wide color theme.
  - Computes an accessible/localized label (aria-label, title, optional
    visible text) via vue-i18n.
  - Supports a `floating` prop for fixed positioning and a `showText`
    prop to display the label next to the icon.

  Out of Scope:
  - Actual theme state management and persistence (delegated to the
    useColorTheme composable in @/lib/theme).
  - Global CSS variable definitions for light/dark palettes (defined
    elsewhere in the design system).

  Dependencies: vue-i18n (useI18n), lucide-vue-next (Moon, Sun icons),
  @/lib/theme (useColorTheme composable).
-->
<template>
  <button
    type="button"
    class="theme-toggle"
    :class="{ 'theme-toggle--floating': floating }"
    :aria-label="label"
    :title="label"
    @click="toggleColorTheme"
  >
    <Sun v-if="isDark" class="theme-toggle__icon" aria-hidden="true" />
    <Moon v-else class="theme-toggle__icon" aria-hidden="true" />
    <span v-if="showText" class="theme-toggle__text">{{ label }}</span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Moon, Sun } from 'lucide-vue-next'
import { useColorTheme } from '@/lib/theme'

defineOptions({
  name: 'ThemeToggle',
})

withDefaults(defineProps<{
  floating?: boolean
  showText?: boolean
}>(), {
  floating: false,
  showText: false,
})

const { t } = useI18n()
const { isDark, toggleColorTheme } = useColorTheme()

const label = computed(() => (
  isDark.value ? t('theme.switchToLight') : t('theme.switchToDark')
))
</script>

<style scoped>
.theme-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  width: 40px;
  min-width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  background: var(--bg-surface);
  color: var(--text-primary);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  transition:
    background var(--duration-base) var(--ease-default),
    border-color var(--duration-base) var(--ease-default),
    box-shadow var(--duration-base) var(--ease-default),
    color var(--duration-base) var(--ease-default),
    transform var(--duration-fast) var(--ease-default);
}

.theme-toggle:hover,
.theme-toggle:focus-visible {
  background: var(--bg-interactive);
  border-color: var(--border-strong);
  box-shadow: var(--shadow-md);
}

.theme-toggle:focus-visible {
  outline: 2px solid var(--border-focus);
  outline-offset: 2px;
}

.theme-toggle--floating {
  position: fixed;
  right: var(--space-6);
  bottom: calc(var(--space-6) + 56px);
  z-index: 1200;
}

.theme-toggle__icon {
  width: 18px;
  height: 18px;
}

.theme-toggle__text {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}
</style>
